/**
 * CR-7400: Buyer ID multi-select for the PO Lines filter panel.
 *
 * The filter used to be a free text box sending buyer_id__icontains, so EB1
 * also returned EB10 and EB11 and the user could neither see which codes
 * existed nor pick more than one. The Supplier Portal has always offered the
 * distinct codes on the customer's PO lines with Select All / Deselect All and
 * matched them exactly (OpenOrdersController.cs:815, POL.BuyerID IN (...)).
 *
 * The selection is held as a comma-separated string so it travels through the
 * panel's existing (key, value) filter plumbing unchanged, and is sent as
 * buyer_id__in. An empty selection means no buyer filter.
 */
import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import { Button } from '../../../../ui/button';
import { Input } from '../../../../ui/input';
import { Checkbox } from '../../../../ui/checkbox';
import { ChevronsUpDown, Search } from 'lucide-react';

interface BuyerIdMultiSelectProps {
  /** Buyer codes in scope (filter-options `buyer_ids`, already sorted). */
  options: string[];
  /** Comma-separated selection, as held in filters.buyerId. Empty = all. */
  value: string;
  /** Called with the new comma-separated selection. */
  onChange: (value: string) => void;
}

/** Split a stored selection into codes, dropping blanks. */
export function parseBuyerIds(value: string): string[] {
  return (value || '')
    .split(',')
    .map((code) => code.trim())
    .filter((code) => code !== '');
}

/**
 * What the grid, the tab counts and the export should send as ``buyer_id__in``
 * for a selection. Empty string means: send no buyer filter at all.
 *
 * A selection covering every offered code is "All", and All must not narrow
 * anything: the options are the codes in USE, so an IN list of all of them
 * still excludes the lines that carry no buyer code, and Select All would
 * quietly return fewer rows than no selection. The Supplier Portal settles it
 * the same way, with an All state that skips the clause outright
 * (OpenOrdersController.cs:811, ``buyerId != "All"``).
 *
 * Codes the options do not carry are kept: the options can lag the selection
 * (a scope switch, a code that left the data), and that selection is a real
 * filter the user can see ticked.
 */
export function buyerIdFilterParam(value: string, options: string[]): string {
  const selected = parseBuyerIds(value);
  if (selected.length === 0) return '';
  const offered = options.filter((code) => code !== '');
  if (offered.length > 0 && offered.every((code) => selected.includes(code))) return '';
  return selected.join(',');
}

export function BuyerIdMultiSelect({ options, value, onChange }: BuyerIdMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = useMemo(() => parseBuyerIds(value), [value]);

  // A selected code need not be in the options: a deep link can carry one,
  // a code can leave the data, and on first render the options have not
  // arrived yet. Those codes are listed after the offered ones so they show
  // as ticked and can be unticked, instead of being silently dropped from
  // the selection on the next click while the filter still holds them.
  const allCodes = useMemo(
    () => [...options, ...selected.filter((c) => !options.includes(c))],
    [options, selected],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCodes;
    return allCodes.filter((code) => code.toLowerCase().includes(q));
  }, [allCodes, query]);

  const toggle = (code: string) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    // Keep the stored order the same as the listed order, so the param a
    // given selection produces does not depend on the click sequence.
    onChange(allCodes.filter((c) => next.includes(c)).join(','));
  };

  const label = useMemo(() => {
    // Both ends of the range read "All buyer IDs" because both filter the
    // same: nothing ticked and everything ticked each send no buyer filter
    // (buyerIdFilterParam), so the label matches what the grid will show.
    if (selected.length === 0) return 'All buyer IDs';
    if (buyerIdFilterParam(value, options) === '') return 'All buyer IDs';
    if (selected.length === 1) return selected[0];
    return `${selected.length} buyer IDs`;
  }, [selected, value, options]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Filter by buyer ID"
          className="w-full justify-between font-normal"
          disabled={allCodes.length === 0}
        >
          <span className="truncate">{allCodes.length === 0 ? 'No buyer IDs' : label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <div className="p-2 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to find a code…"
              aria-label="Search buyer IDs"
              className="pl-7 h-8"
            />
          </div>
          <div className="flex items-center gap-2">
            {/* Select All ticks every code, and buyerIdFilterParam turns that
                into no buyer filter, so no line is skipped. The two have to be
                separate: the options are the codes in USE, so an IN list of
                all of them still excludes the lines whose buyer code is blank
                and Select All would return fewer rows than no selection. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange(allCodes.join(','))}
              title="Every buyer ID, including the lines that carry no code"
            >
              Select All
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onChange('')}
            >
              Deselect All
            </Button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {visible.map((code) => (
            <label
              key={code}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
            >
              <Checkbox
                checked={selected.includes(code)}
                onCheckedChange={() => toggle(code)}
                aria-label={code}
              />
              <span className="truncate font-mono">{code}</span>
            </label>
          ))}
          {visible.length === 0 && (
            <div className="px-2 py-4 text-sm text-muted-foreground text-center">
              No codes match “{query.trim()}”
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
